import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { Check, Settings, InfoOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import api from "../api/axios";
import LockIcon from "@mui/icons-material/Lock";
import PracticeConfigDialog from "../components/PracticeConfigDialog";
import MonthCalendarNav from "../components/MonthCalendarNav";
import { useMonthParam } from "../hooks/useCalendarUrlState";
import PullToRefresh from "react-simple-pull-to-refresh";
import { useDarkMode } from "../App";
import { useDialog } from "../contexts/DialogProvider";
import { hhmm } from "../utils/timeFormat";

const PRACTICE_ROOMS = [
  "Lesson 1", "Lesson 2", "Lesson 3", "Lesson 4", "Lesson 5", "Lesson 6"
];

function getEndTime(start, duration = 30) {
  if (!start) return "";
  let [h, m] = start.split(":").map(Number);
  m += duration;
  h += Math.floor(m / 60);
  m = m % 60;
  if (h > 23) h = 23;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function ConfirmDialog({ open, title, message, onConfirm, onCancel, darkMode }) {
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogContent sx={{ pt: 4, textAlign: "center" }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>{title}</Typography>
        <Typography variant="body2" sx={{ color: darkMode ? "#aaa" : "#666", mb: 4 }}>{message}</Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onCancel} variant="outlined" sx={{ flex: 1 }}>취소</Button>
        <Button onClick={onConfirm} variant="contained" sx={{ flex: 1 }}>확인</Button>
      </DialogActions>
    </Dialog>
  );
}

function PracticePage({ user }) {
  const { darkMode } = useDarkMode();
  const { toast } = useDialog();
  const navigate = useNavigate();
  const [year, month, setYearMonth] = useMonthParam(); // 보던 달은 URL(?month=)에 유지
  const [accessDeniedDialog, setAccessDeniedDialog] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [confirmDialog, setConfirmDialog] = useState({ open: false });
  const [configDialog, setConfigDialog] = useState(false);
  const [practiceConfig, setPracticeConfig] = useState(null);
  const [effectiveConfig, setEffectiveConfig] = useState(null);
  const [overrideDates, setOverrideDates] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const today = dayjs();

  const timeSelectionRef = useRef(null);
  const roomSelectionRef = useRef(null);
  // 날짜를 연속으로 눌렀을 때 늦게 도착한 이전 요청이 화면을 덮어쓰지 않도록 하는 순번
  const loadSeqRef = useRef(0);
  const timeSectionRevealedRef = useRef(false);

  useEffect(() => {
    fetchPracticeConfig();
    fetchOverrideDates();
  }, []);

  useEffect(() => {
    if (user?.role !== "ADMIN" && user?.role !== "TEACHER" && !user?.practice_room_access) {
      setAccessDeniedDialog(true);
    }
  }, [user]);

  // 휴일 목록 조회
  useEffect(() => {
    const startDate = dayjs(`${year}-${month + 1}-01`).subtract(1, 'month').format('YYYY-MM-DD');
    const endDate = dayjs(`${year}-${month + 1}-01`).add(2, 'month').format('YYYY-MM-DD');
    
    api.get("/holidays", { params: { start_date: startDate, end_date: endDate } })
      .then(res => setHolidays(res.data))
      .catch(() => setHolidays([]));
  }, [year, month]);

  const isHoliday = (dateStr) => {
    return holidays.some(h => h.date === dateStr);
  };

  const getHolidayReason = (dateStr) => {
    const holiday = holidays.find(h => h.date === dateStr);
    return holiday?.reason || '휴일';
  };

  // 날짜가 바뀌면 유효 설정과 예약 목록을 함께 받아 한 번에 반영한다.
  // 따로 반영하면 시간 슬롯 상태가 두 번 바뀌며 화면이 덜그럭거린다.
  useEffect(() => {
    if (!selectedDate) return;
    const seq = ++loadSeqRef.current;
    setSlotsLoading(true);
    Promise.all([
      api.get("/practice-config/effective", { params: { date: selectedDate } })
        .then(res => res.data)
        .catch(err => { console.error("유효 설정 로드 실패:", err); return null; }),
      api.get("/practices", { params: { date: selectedDate } })
        .then(res => res.data)
        .catch(() => []),
    ]).then(([config, list]) => {
      if (seq !== loadSeqRef.current) return; // 더 최근 요청이 있으면 버린다
      if (config) setEffectiveConfig(config);
      setReservations(list);
      setSlotsLoading(false);
    });
  }, [selectedDate]);

  // 시간 선택 영역이 처음 나타날 때만 스크롤 — 이후 날짜를 바꿀 때는 화면을 움직이지 않는다
  useEffect(() => {
    if (!selectedDate || slotsLoading || timeSectionRevealedRef.current) return;
    if (!timeSelectionRef.current) return;
    timeSectionRevealedRef.current = true;
    timeSelectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [selectedDate, slotsLoading]);

  useEffect(() => {
    if (selectedTime && roomSelectionRef.current) {
      setTimeout(() => {
        roomSelectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, [selectedTime]);

  const fetchPracticeConfig = async () => {
    try {
      const res = await api.get("/practice-config");
      setPracticeConfig(res.data);
    } catch (err) {
      console.error("설정 로드 실패:", err);
      setPracticeConfig({
        available_days: [0, 1, 2, 3, 4, 5, 6],
        start_time: "12:00",
        end_time: "18:00",
        slot_duration: 30,
        lesson_restricted_rooms: ["Lesson 1", "Lesson 2"],
        lesson_restricted_times: ["12:00", "12:30", "13:00", "13:30", "14:00", "14:30", "15:00", "15:30", "16:00"],
        lesson_restricted_days: [6],
      });
    }
  };

  const fetchOverrideDates = async () => {
    try {
      const res = await api.get("/practice-config/overrides");
      setOverrideDates(res.data.map(o => ({
        date: o.date,
        is_open: o.is_open,
        // 개방 시간을 실제로 바꾼 예외인지 (고정 차단 해제만 있는 행은 달력 점 표시 제외)
        has_time_override: !!(o.start_time || o.end_time),
        weekday_same_day_only: o.weekday_same_day_only,
        weekday_time_restricted: o.weekday_time_restricted,
        weekday_booking_start_hour: o.weekday_booking_start_hour,
        weekday_one_hour_limit: o.weekday_one_hour_limit,
      })));
    } catch (err) {
      console.error("예외 설정 로드 실패:", err);
    }
  };

  const fetchEffectiveConfig = async (date) => {
    try {
      const res = await api.get("/practice-config/effective", { params: { date } });
      setEffectiveConfig(res.data);
    } catch (err) {
      console.error("유효 설정 로드 실패:", err);
    }
  };

  const generateTimeSlots = () => {
    if (!effectiveConfig || !selectedDate) return [];
    if (effectiveConfig.is_open === false) return [];

    const startTime = effectiveConfig.start_time?.slice(0, 5);
    const endTime = effectiveConfig.end_time?.slice(0, 5);
    if (!startTime || !endTime) return [];

    const [startH, startM] = startTime.split(":").map(Number);
    const [endH, endM] = endTime.split(":").map(Number);
    const slotDuration = effectiveConfig.slot_duration || 30;

    const slots = [];
    for (let m = startH * 60 + startM; m < endH * 60 + endM; m += slotDuration) {
      const h = Math.floor(m / 60);
      const min = m % 60;
      const timeStr = `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
      slots.push(timeStr);
    }
    return slots;
  };

  const isTimeUnavailable = (time) => {
    const unavailableTimes = effectiveConfig?.unavailable_times || [];
    return unavailableTimes.includes(time);
  };

  const isRoomAvailableAtUnavailableTime = (room) => {
    const availableRooms = effectiveConfig?.available_rooms || [];
    return availableRooms.includes(room);
  };

  const isRoomUnavailable = (room, time = null) => {
    // 전체 시간대에 사용 불가능한 연습실 (레거시)
    const unavailableRooms = effectiveConfig?.unavailable_rooms || [];
    if (unavailableRooms.includes(room)) return true;

    // 시간대별 사용 불가능한 연습실
    if (time) {
      const unavailableRoomsByTime = effectiveConfig?.unavailable_rooms_by_time || {};
      const unavailableRoomsForTime = unavailableRoomsByTime[time] || [];
      if (unavailableRoomsForTime.includes(room)) return true;
    }

    return false;
  };

  // 관리자가 차단 모드에서 이 날짜에 한해 해제한 고정 차단 슬롯 (해제된 슬롯은 예약 가능)
  const isReleasedSlot = (room, time) => {
    const releasedSlots = effectiveConfig?.released_slots || [];
    return releasedSlots.some(s => s.room_name === room && (s.start_time || "").slice(0, 5) === time);
  };

  const TIME_SLOTS = generateTimeSlots();

  function isLessonRoomRestricted(room, time) {
    const config = effectiveConfig || practiceConfig;
    if (!config) return false;
    if (user?.role === 'ADMIN' || user?.role === 'TEACHER') return false;
    if (!config.lesson_restricted) return false;

    const restrictedRooms = config.lesson_restricted_rooms || [];
    const restrictedTimes = config.lesson_restricted_times || [];

    if (!restrictedRooms.includes(room) || !restrictedTimes.includes(time)) return false;

    const myReservation = reservations.find(
      r => r.room_name === room && r.start_time.slice(0, 5) === time && r.user_id === user.id
    );
    if (myReservation) return false;
    return true;
  }

  function hasAnyAvailableRoomAt(time) {
    if (user?.role === 'ADMIN' || user?.role === 'TEACHER') return true;

    return PRACTICE_ROOMS.some(room => {
      // 날짜별 해제된 고정 차단 슬롯은 사용 불가 검사(1~3)를 건너뜀
      const released = isReleasedSlot(room, time);

      // 1. unavailable_times 체크 (available_rooms 예외 포함)
      const isUnavailableTime = !released && (effectiveConfig?.unavailable_times || []).includes(time);
      const isRoomException = (effectiveConfig?.available_rooms || []).includes(room);
      if (isUnavailableTime && !isRoomException) return false;

      // 2. unavailable_rooms (레거시) 체크
      if (!released && (effectiveConfig?.unavailable_rooms || []).includes(room)) return false;

      // 3. unavailable_rooms_by_time 체크
      const unavailableByTime = effectiveConfig?.unavailable_rooms_by_time || {};
      if (!released && (unavailableByTime[time] || []).includes(room)) return false;

      // 4. 레슨실 제한 체크
      if (isLessonRoomRestricted(room, time)) return false;

      // 5. 이미 예약됨 체크
      if (reservations.some(r => r.room_name === room && r.start_time.slice(0, 5) === time)) return false;

      return true;
    });
  }

  function isDateAvailable(dateObj) {
    if (!practiceConfig) return false;

    const dateStr = dateObj.format("YYYY-MM-DD");

    // 연습실 예외(override)를 휴일보다 우선 적용 (열림 예외면 휴일이어도 예약 가능)
    const override = overrideDates.find(o => o.date === dateStr);
    if (override) return override.is_open;

    // 예외가 없는 휴일은 닫힘
    if (isHoliday(dateStr)) return false;

    const dow = dateObj.day();
    return practiceConfig.available_days.includes(dow);
  }

  // 평일(월~금) 예약 가능 여부 체크 (회원 전용)
  // 테스트 모드에서는 이 제한을 우회
  function isWeekdayReservationAllowed(dateObj) {
    if (process.env.REACT_APP_TEST_BYPASS_WEEKDAY_CHECK === 'true') return true;
    if (user?.role === 'ADMIN' || user?.role === 'TEACHER') return true;

    const dow = dateObj.day();
    const isWeekday = dow >= 1 && dow <= 5; // 월(1) ~ 금(5)

    if (!isWeekday) return true; // 주말은 제한 없음

    // 오픈 예외일(휴일 여부 무관)은 예외 설정에 따라 평일 제한을 항목별로 해제
    const dateStr = dateObj.format("YYYY-MM-DD");
    const override = overrideDates.find(o => o.date === dateStr);
    const hasOpenOverride = override?.is_open === true;
    const applySameDayOnly = !hasOpenOverride || override.weekday_same_day_only !== false;
    const applyBookingTime = !hasOpenOverride || override.weekday_time_restricted !== false;
    const startHour = (hasOpenOverride && override.weekday_booking_start_hour != null)
      ? override.weekday_booking_start_hour
      : (practiceConfig?.weekday_booking_start_hour ?? 12);

    // 한국 시간 기준으로 계산
    const koreaTime = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Seoul' }));
    const koreaTodayStr = koreaTime.toISOString().slice(0, 10);

    const isToday = dateStr === koreaTodayStr;
    if (applySameDayOnly && !isToday) return false; // 평일은 당일만 가능

    const currentHour = koreaTime.getHours();
    if (applyBookingTime && currentHour < startHour) return false;
    return true;
  }

  // 평일 예약 불가 사유
  function getWeekdayRestrictionReason(dateObj) {
    const dow = dateObj.day();
    const isWeekday = dow >= 1 && dow <= 5;

    if (!isWeekday) return null;

    // 오픈 예외일(휴일 여부 무관)은 예외 설정에 따라 평일 제한을 항목별로 해제
    const dateStr = dateObj.format("YYYY-MM-DD");
    const override = overrideDates.find(o => o.date === dateStr);
    const hasOpenOverride = override?.is_open === true;
    const applySameDayOnly = !hasOpenOverride || override.weekday_same_day_only !== false;
    const applyBookingTime = !hasOpenOverride || override.weekday_time_restricted !== false;
    const startHour = (hasOpenOverride && override.weekday_booking_start_hour != null)
      ? override.weekday_booking_start_hour
      : (practiceConfig?.weekday_booking_start_hour ?? 12);

    // 한국 시간 기준으로 계산
    const koreaTime = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Seoul' }));
    const koreaTodayStr = koreaTime.toISOString().slice(0, 10);

    const isToday = dateStr === koreaTodayStr;
    if (applySameDayOnly && !isToday) return "당일 예약 가능";

    const currentHour = koreaTime.getHours();
    if (applyBookingTime && currentHour < startHour) return `${startHour}시부터 예약 가능`;

    return null;
  }

  // 평일 예약 제한 안내 문구 (표시상 '예약 가능'이지만 아직 예약 불가할 때 클릭 토스트용)
  function getWeekdayRestrictionMessage(dateObj) {
    const dow = dateObj.day();
    const isWeekday = dow >= 1 && dow <= 5;
    if (!isWeekday) return null;

    const dateStr = dateObj.format("YYYY-MM-DD");
    const override = overrideDates.find(o => o.date === dateStr);
    const hasOpenOverride = override?.is_open === true;
    const applySameDayOnly = !hasOpenOverride || override.weekday_same_day_only !== false;
    const applyBookingTime = !hasOpenOverride || override.weekday_time_restricted !== false;
    const startHour = (hasOpenOverride && override.weekday_booking_start_hour != null)
      ? override.weekday_booking_start_hour
      : (practiceConfig?.weekday_booking_start_hour ?? 12);

    const koreaTime = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Seoul' }));
    const koreaTodayStr = koreaTime.toISOString().slice(0, 10);
    const isToday = dateStr === koreaTodayStr;

    if ((applySameDayOnly && !isToday) || (applyBookingTime && koreaTime.getHours() < startHour)) {
      return `평일 연습실은 당일 ${startHour}시 이후에 예약이 가능합니다.`;
    }
    return null;
  }

  function hasOverride(dateObj) {
    const dateStr = dateObj.format("YYYY-MM-DD");
    // 휴무 예외이거나 개방 시간이 바뀐 예외만 표시 — 고정 차단 해제로 생긴 행은 제외
    return overrideDates.some(o => o.date === dateStr && (!o.is_open || o.has_time_override));
  }

  const firstDay = dayjs(`${year}-${month + 1}-01`);
  const firstDow = firstDay.day();
  const lastDate = firstDay.endOf("month").date();
  const prevMonthLastDate = firstDay.subtract(1, "month").endOf("month").date();
  const calendarDays = [];

  for (let i = firstDow - 1; i >= 0; i--) {
    calendarDays.push({
      day: prevMonthLastDate - i,
      isCurrentMonth: false,
      month: month === 0 ? 11 : month - 1,
      year: month === 0 ? year - 1 : year
    });
  }

  for (let day = 1; day <= lastDate; day++) {
    calendarDays.push({ day, isCurrentMonth: true, month, year });
  }

  const remainingDays = 42 - calendarDays.length;
  for (let day = 1; day <= remainingDays; day++) {
    calendarDays.push({
      day,
      isCurrentMonth: false,
      month: month === 11 ? 0 : month + 1,
      year: month === 11 ? year + 1 : year
    });
  }

  function handleDateClick(dateInfo) {
    if (!practiceConfig) return;
    const { day, month: m, year: y } = dateInfo;
    const clickedDate = dayjs(`${y}-${m + 1}-${day}`);
    const dateStr = clickedDate.format("YYYY-MM-DD");

    // 휴일 체크 (열림 예외가 있으면 휴일이어도 허용)
    const override = overrideDates.find(o => o.date === dateStr);
    if (isHoliday(dateStr) && !override?.is_open) return;

    const canSelectPast = user?.role === 'ADMIN' || user?.role === 'TEACHER';
    const isPast = clickedDate.isBefore(today, "day");
    const isAvailable = isDateAvailable(clickedDate);

    if (isAvailable && (canSelectPast || !isPast)) {
      // 이미 선택된 날짜를 다시 눌러도 선택은 유지 (시간/연습실 선택 영역을 닫지 않음)
      if (dateStr === selectedDate) return;
      // effectiveConfig 는 비우지 않는다 — 비우면 아래 영역이 통째로 사라졌다가
      // 응답이 온 뒤 다시 생기면서 스크롤 위치가 튄다. 로딩 중에는 흐리게만 표시.
      setSelectedDate(dateStr);
      setSelectedTime(null);
    }
  }

  function handleTimeClick(time) {
    setSelectedTime(time);
  }

  async function handleRoomSelect(room) {
    const slotDuration = practiceConfig?.slot_duration || 30;
    setConfirmDialog({
      open: true,
      title: `${room} 예약`,
      message: `${selectedDate} ${selectedTime}~${getEndTime(selectedTime, slotDuration)}에 예약합니다.`,
      action: async () => {
        try {
          await api.post("/practices", {
            room_name: room,
            date: selectedDate,
            start_time: selectedTime,
            end_time: getEndTime(selectedTime, slotDuration),
          });
          toast("연습실 예약이 완료되었습니다.");
          const res = await api.get("/practices", { params: { date: selectedDate } });
          setReservations(res.data);
        } catch (err) {
          toast(err?.response?.data?.message || "예약 실패", "error");
        }
      }
    });
  }

  async function handleCancel(reservationId) {
    setConfirmDialog({
      open: true,
      title: "예약 취소",
      message: "이 예약을 취소하시겠습니까?",
      action: async () => {
        try {
          await api.delete(`/practices/${reservationId}`);
          toast("예약이 취소되었습니다.");
          const res = await api.get("/practices", { params: { date: selectedDate } });
          setReservations(res.data);
        } catch (err) {
          toast(err?.response?.data?.message || "예약 취소 실패", "error");
        }
      }
    });
  }

  // 달 이동 — 점프로 선택 날짜가 화면 밖으로 나가면 아래 시간/연습실 선택 영역을 닫는다.
  // (◀▶/스와이프 한 칸 이동은 기존처럼 선택 유지)
  function handleMonthChange(y, m, { jump }) {
    setYearMonth(y, m);
    if (jump && selectedDate && !dayjs(selectedDate).isSame(dayjs(new Date(y, m, 1)), "month")) {
      setSelectedDate(null);
      setSelectedTime(null);
    }
  }

  function goToDetailPage() {
    if (selectedDate) navigate(`/practice/${selectedDate}`);
  }

  const handleConfigSave = () => {
    fetchPracticeConfig();
    fetchOverrideDates();
    if (selectedDate) {
      fetchEffectiveConfig(selectedDate);
    }
  };

  // Pull-to-Refresh 핸들러
  const handleRefresh = useCallback(async () => {
    await Promise.all([
      fetchPracticeConfig(),
      fetchOverrideDates(),
    ]);

    // 휴일 다시 로드
    const startDate = dayjs(`${year}-${month + 1}-01`).subtract(1, 'month').format('YYYY-MM-DD');
    const endDate = dayjs(`${year}-${month + 1}-01`).add(2, 'month').format('YYYY-MM-DD');

    try {
      const res = await api.get("/holidays", { params: { start_date: startDate, end_date: endDate } });
      setHolidays(res.data);
    } catch {
      setHolidays([]);
    }

    // 선택된 날짜가 있으면 해당 날짜 데이터도 새로고침
    if (selectedDate) {
      await fetchEffectiveConfig(selectedDate);
      try {
        const res = await api.get("/practices", { params: { date: selectedDate } });
        setReservations(res.data);
      } catch {
        setReservations([]);
      }
    }
  }, [year, month, selectedDate]);

  if (user?.role !== "ADMIN" && user?.role !== "TEACHER" && !user?.practice_room_access) {
    return (
      <Dialog open={accessDeniedDialog} onClose={() => navigate("/notice")}>
        <DialogTitle>연습실 예약 페이지 사용 불가</DialogTitle>
        <DialogContent>
          <Typography>연습실 이용 비용을 결제하신 회원만 사용 가능합니다.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => navigate("/notice")} variant="contained">공지사항으로</Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (!practiceConfig) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>;
  }

  return (
    <PullToRefresh
      onRefresh={handleRefresh}
      pullingContent={
        <Box sx={{ textAlign: "center", py: 2, color: darkMode ? "#aaa" : "#666" }}>
          ↓ 당겨서 새로고침
        </Box>
      }
      refreshingContent={
        <Box sx={{ textAlign: "center", py: 2, color: "primary.main" }}>
          새로고침 중...
        </Box>
      }
    >
    <Box sx={{ pt: { xs: 1, sm: 2 }, pb: 2, maxWidth: 900, mx: "auto", px: { xs: 1, sm: 2 } }}>
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={() => { confirmDialog.action?.(); setConfirmDialog({ ...confirmDialog, open: false }); }}
        onCancel={() => setConfirmDialog({ ...confirmDialog, open: false })}
        darkMode={darkMode}
      />

      <PracticeConfigDialog
        open={configDialog}
        onClose={() => setConfigDialog(false)}
        onSave={handleConfigSave}
      />

      {/* 모바일은 헤더에 페이지 제목이 표시되므로 본문 제목을 숨기고, 관리자 설정 버튼만 남긴다 */}
      <Box sx={{ display: { xs: user?.role === "ADMIN" ? "flex" : "none", sm: "flex" }, justifyContent: { xs: "flex-end", sm: "center" }, alignItems: "center", mb: { xs: 1, sm: 3 }, position: "relative" }}>
        <Typography variant="h5" fontWeight="bold" sx={{ display: { xs: "none", sm: "block" } }}>연습실 예약</Typography>
        {user?.role === "ADMIN" && (
          <IconButton 
            onClick={() => setConfigDialog(true)} 
            color="primary"
            sx={{ position: { xs: "static", sm: "absolute" }, right: 0 }}
          >
            <Settings />
          </IconButton>
        )}
      </Box>

      <Paper
        elevation={2}
        sx={{
          px: { xs: 1, sm: 3 },
          py: { xs: 2, sm: 3 },
          mb: 3,
          overflow: 'hidden',
        }}
      >
        <MonthCalendarNav year={year} month={month} onChange={handleMonthChange}>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 1, mb: 1 }}>
            {["일", "월", "화", "수", "목", "금", "토"].map((d, i) => (
              <Box key={i} sx={{ textAlign: "center", fontWeight: "bold", color: i === 0 ? "#d32f2f" : i === 6 ? "#1976d2" : "inherit" }}>{d}</Box>
            ))}
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 1 }}>
          {calendarDays.map((dateInfo, idx) => {
            const { day, isCurrentMonth, month: m, year: y } = dateInfo;
            const dateObj = dayjs(`${y}-${m + 1}-${day}`);
            const dow = dateObj.day();
            const isToday = dateObj.isSame(today, "day");
            const isPast = dateObj.isBefore(today, "day");
            
            const canSelectPast = user?.role === 'ADMIN' || user?.role === 'TEACHER';
            const dateStr = dateObj.format("YYYY-MM-DD");
            
            const override = overrideDates.find(o => o.date === dateStr);
            // 열림 예외가 있으면 휴일 표기/차단을 해제 (예외 우선)
            const holidayDate = isHoliday(dateStr) && !override?.is_open;
            const holidayReason = holidayDate ? getHolidayReason(dateStr) : null;
            
            const isAvailable = isDateAvailable(dateObj);
            const isOverride = hasOverride(dateObj);
            const weekdayReason = getWeekdayRestrictionReason(dateObj);
            const isClickable = isAvailable && (canSelectPast || !isPast) && !holidayDate;
            const isSelected = selectedDate === dateStr;

            const cellContent = (
              <Box
                key={idx}
                onClick={() => isClickable && handleDateClick(dateInfo)}
                sx={{
                  aspectRatio: "1",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: isClickable ? "pointer" : "default",
                  borderRadius: 2,
                  bgcolor: isSelected
                    ? (darkMode ? "#1565c0" : "#1976d2")
                    : holidayDate
                      ? (darkMode ? "rgba(211, 47, 47, 0.14)" : "#ffebee")
                      : isToday
                        ? (darkMode ? "rgba(255, 152, 0, 0.10)" : "#fff8e1")
                        : isClickable
                          ? (darkMode ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.035)")
                          : "transparent",
                  color: isSelected
                    ? "#fff"
                    : holidayDate
                      ? (darkMode ? "#e57373" : "#d32f2f")
                      : !isCurrentMonth
                        ? (darkMode ? "#666" : "#ccc")
                        : dow === 0
                          ? "#d32f2f"
                          : dow === 6
                            ? "#1976d2"
                            : "inherit",
                  fontWeight: isToday || isSelected || holidayDate ? "bold" : "normal",
                  opacity: isPast && isCurrentMonth && !canSelectPast ? 0.4 : 1,
                  border: isToday ? "1.5px solid #ff9800" : "none",
                  position: "relative",
                }}
              >
                {day}
                {isOverride && isCurrentMonth && !holidayDate && (
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 2,
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      bgcolor: isAvailable ? "#4caf50" : "#f44336",
                    }}
                  />
                )}
              </Box>
            );

            // 툴팁 표시: 휴일 또는 평일 제한
            const showTooltip = isCurrentMonth && (holidayDate || weekdayReason);
            const tooltipTitle = holidayDate ? holidayReason : weekdayReason;

            return showTooltip ? (
              <Tooltip key={idx} title={tooltipTitle} arrow placement="top">
                {cellContent}
              </Tooltip>
            ) : cellContent;
          })}
          </Box>
        </MonthCalendarNav>

          {/* 범례 */}
          <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, bgcolor: darkMode ? "rgba(211, 47, 47, 0.14)" : "#ffebee", border: "1px solid #d32f2f", borderRadius: 0.5 }} />
              <Typography variant="caption" color="text.secondary">휴일</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, bgcolor: darkMode ? "rgba(255, 152, 0, 0.10)" : "#fff8e1", border: "1.5px solid #ff9800", borderRadius: 0.5 }} />
              <Typography variant="caption" color="text.secondary">오늘</Typography>
            </Box>
          </Box>
      </Paper>

      {/* 연습실 이용 안내 — 월 이동 애니메이션과 무관한 정적 안내라 달력 카드 밖에 둔다 */}
      {user?.role === 'MEMBER' && (
        <Box
          sx={{
            mb: 3,
            display: "flex",
            borderRadius: 1.5,
            overflow: "hidden",
            bgcolor: darkMode ? "rgba(25, 118, 210, 0.08)" : "#f5f9ff",
            border: darkMode ? "1px solid rgba(25, 118, 210, 0.2)" : "1px solid #e3f2fd",
          }}
        >
          {/* 왼쪽 accent 바 */}
          <Box sx={{ flexShrink: 0, width: 4, bgcolor: darkMode ? "#90caf9" : "#1976d2" }} />
          <Box sx={{ flex: 1, minWidth: 0, px: 1, py: 1.25 }}>
            {/* 헤더 */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
              <InfoOutlined sx={{ fontSize: "1.05rem", color: darkMode ? "#90caf9" : "#1976d2" }} />
              <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", color: darkMode ? "#90caf9" : "#1565c0" }}>
                연습실 이용 안내
              </Typography>
            </Box>
            {/* 규칙 */}
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
              <Box sx={{ flexShrink: 0, mt: "8px", width: 4, height: 4, borderRadius: "50%", bgcolor: darkMode ? "rgba(144, 202, 249, 0.8)" : "#1976d2" }} />
              <Typography variant="body2" sx={{ flex: 1, lineHeight: 1.65, textAlign: "left", wordBreak: "keep-all", overflowWrap: "break-word", color: darkMode ? "#c9d6e5" : "#37455a" }}>
                평일은 <Box component="span" sx={{ fontWeight: 700, color: darkMode ? "#90caf9" : "#1565c0" }}>당일 {practiceConfig?.weekday_booking_start_hour ?? 12}시 이후</Box>에 예약이 가능하며, <Box component="span" sx={{ fontWeight: 700, color: darkMode ? "#90caf9" : "#1565c0" }}>최대 1시간</Box>(30분 2회) 이용 가능합니다.
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75, mt: 0.5 }}>
              <Box sx={{ flexShrink: 0, mt: "8px", width: 4, height: 4, borderRadius: "50%", bgcolor: darkMode ? "rgba(144, 202, 249, 0.8)" : "#1976d2" }} />
              <Typography variant="body2" sx={{ flex: 1, lineHeight: 1.65, textAlign: "left", wordBreak: "keep-all", overflowWrap: "break-word", color: darkMode ? "#c9d6e5" : "#37455a" }}>
                토요일은 자유롭게 예약할 수 있습니다.
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {selectedDate && effectiveConfig && (
        <>
          {effectiveConfig.is_override && effectiveConfig.has_time_override && (
            <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: darkMode ? "rgba(255, 243, 224, 0.15)" : "#fff3e0" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Chip label="예외 설정 적용" size="small" color="warning" />
                <Typography variant="body2">
                  {hhmm(effectiveConfig.start_time)} ~ {hhmm(effectiveConfig.end_time)}
                  {!effectiveConfig.lesson_restricted && " | 레슨실 제한 없음"}
                </Typography>
              </Box>
              {effectiveConfig.override_reason && (
                <Typography variant="caption" color="text.secondary">
                  사유: {effectiveConfig.override_reason}
                </Typography>
              )}
            </Paper>
          )}

          <Box ref={timeSelectionRef} sx={{ mb: 4, scrollMarginTop: "64px" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
              <Box>
                <Typography variant="body2" sx={{ color: "#1976d2", fontWeight: 700, mb: 0.5, fontSize: "0.85rem" }}>STEP 1</Typography>
                <Typography variant="h6" fontWeight="bold">시간을 선택하세요</Typography>
              </Box>
              {(user?.role === "ADMIN" || user?.role === "TEACHER" || user?.role === "MEMBER") && (
                <Button variant="outlined" size="small" onClick={goToDetailPage}>전체 예약 확인</Button>
              )}
            </Box>

            <Box
              sx={{
                maxWidth: "800px",
                mx: "auto",
                display: "grid",
                gridTemplateColumns: { xs: "repeat(3, 1fr)", sm: "repeat(4, 1fr)" },
                gap: 1.5,
                // 다른 날짜를 불러오는 동안은 흐리게 + 클릭 차단 (영역은 그대로 유지)
                opacity: slotsLoading ? 0.4 : 1,
                pointerEvents: slotsLoading ? "none" : "auto",
                transition: "opacity 0.15s ease",
              }}
            >
              {TIME_SLOTS.map(time => {
                const isSelected = selectedTime === time;
                const hasMyReservationAtTime = reservations.some(
                  r => r.start_time.slice(0, 5) === time && r.user_id === user.id
                );
                const isFullyUnavailable = !hasMyReservationAtTime && !hasAnyAvailableRoomAt(time);
                const isClickable = !isFullyUnavailable || (user?.role === 'ADMIN' || user?.role === 'TEACHER');
                const slotDuration = practiceConfig?.slot_duration || 30;

                return (
                  <Box
                    key={time}
                    onClick={() => isClickable && handleTimeClick(time)}
                    sx={{
                      p: { xs: 1.25, sm: 2 },
                      borderRadius: 2,
                      minWidth: 0,
                      cursor: isClickable ? "pointer" : "not-allowed",
                      background: isSelected
                        ? "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)"
                        : hasMyReservationAtTime
                          ? (darkMode ? "rgba(144, 202, 249, 0.15)" : "#e3f2fd")
                          : isFullyUnavailable
                            ? (darkMode ? "grey.800" : "#f5f5f5")
                            : (darkMode ? "grey.900" : "#fff"),
                      color: isSelected ? "#fff" : isFullyUnavailable && !hasMyReservationAtTime ? (darkMode ? "#666" : "#999") : (darkMode ? "#e0e0e0" : "#333"),
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                      border: isSelected
                        ? "2px solid #1565c0"
                        : hasMyReservationAtTime
                          ? "2px solid #1976d2"
                          : isFullyUnavailable
                            ? (darkMode ? "2px solid rgba(255, 255, 255, 0.12)" : "2px solid #e0e0e0")
                            : (darkMode ? "2px solid rgba(255, 255, 255, 0.12)" : "2px solid #e8e8e8"),
                      boxShadow: isSelected ? "0 4px 12px rgba(25, 118, 210, 0.3)" : "none",
                      opacity: isFullyUnavailable && !(user?.role === 'ADMIN' || user?.role === 'TEACHER') ? 0.6 : 1,
                      transition: "box-shadow 0.2s ease, border-color 0.2s ease",
                      "&:hover": isClickable && !isSelected ? {
                        boxShadow: darkMode ? "0 4px 12px rgba(0, 0, 0, 0.4)" : "0 4px 12px rgba(0, 0, 0, 0.1)"
                      } : {}
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                      <Typography fontWeight={700} sx={{ fontSize: { xs: "0.95rem", sm: "1.05rem" }, lineHeight: 1.2 }}>{time}</Typography>
                      {isSelected && <Check sx={{ fontSize: "1rem" }} />}
                    </Box>
                    <Typography sx={{ opacity: 0.7, mt: 0.25, fontSize: { xs: "0.6rem", sm: "0.7rem" }, whiteSpace: "nowrap" }}>
                      {getEndTime(time, slotDuration)} 까지
                    </Typography>
                    <Typography sx={{
                      color: isSelected ? "inherit" : hasMyReservationAtTime ? "#1976d2" : isFullyUnavailable ? "inherit" : "#4caf50",
                      fontWeight: hasMyReservationAtTime ? 600 : 500,
                      opacity: isFullyUnavailable && !hasMyReservationAtTime ? 0.8 : 1,
                      mt: 0.25,
                      fontSize: { xs: "0.6rem", sm: "0.68rem" },
                      whiteSpace: "nowrap",
                    }}>
                      {hasMyReservationAtTime ? "내 예약 있음" : isFullyUnavailable ? "예약 불가" : "예약 가능"}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </>
      )}

      {selectedDate && selectedTime && effectiveConfig && (
        <Box ref={roomSelectionRef} sx={{ scrollMarginTop: "64px", minHeight: "50vh" }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ color: "#1976d2", fontWeight: 700, mb: 0.5 }}>STEP 2</Typography>
            <Typography variant="h6" fontWeight="bold">연습실을 선택하세요</Typography>
          </Box>

          <Box
            sx={{
              maxWidth: "800px",
              mx: "auto",
              display: "grid",
              gridTemplateColumns: { xs: "repeat(3, 1fr)", sm: "repeat(4, 1fr)" },
              gap: { xs: 1.5, sm: 2 },
            }}
          >
            {PRACTICE_ROOMS.map((room) => {
              const existing = reservations.find(r => r.room_name === room && r.start_time.slice(0, 5) === selectedTime);
              const isRestricted = isLessonRoomRestricted(room, selectedTime);
              const released = isReleasedSlot(room, selectedTime);
              const isUnavailableTime = !released && isTimeUnavailable(selectedTime);
              const isRoomException = isRoomAvailableAtUnavailableTime(room);
              const isRoomBlocked = !released && isRoomUnavailable(room, selectedTime);
              const isMine = existing && existing.user_id === user.id;
              const isAdmin = user?.role === "ADMIN" || user?.role === "TEACHER";

              // 평일 예약 가능 여부 체크
              const selectedDateObj = dayjs(selectedDate);
              const weekdayAllowed = isWeekdayReservationAllowed(selectedDateObj);

              // 사용 불가 시간/연습실이더라도 본인 예약이 있으면 표시
              const isAvailable = !existing && !isRestricted && (!isRoomBlocked || isAdmin) && (!isUnavailableTime || isAdmin || isRoomException) && weekdayAllowed;

              let statusColor = "#4caf50";
              let statusLabel = "예약 가능";
              let isClickable = true;
              let backgroundColor = darkMode ? "grey.900" : "#fff";
              let iconComponent = <Check sx={{ color: statusColor, fontSize: "1.4rem" }} />;

              // 본인 예약은 사용 불가 시간/연습실이더라도 우선 표시
              if (isMine) {
                statusColor = "#1976d2"; statusLabel = "내 예약"; backgroundColor = darkMode ? "rgba(144, 202, 249, 0.15)" : "#e3f2fd";
                iconComponent = <Check sx={{ color: statusColor, fontSize: "1.4rem" }} />;
              } else if (isRoomBlocked && !isAdmin) {
                statusColor = "#9e9e9e"; statusLabel = "사용 불가"; isClickable = false; backgroundColor = darkMode ? "grey.800" : "#f5f5f5";
                iconComponent = <LockIcon sx={{ color: statusColor, fontSize: "1.4rem" }} />;
              } else if (isUnavailableTime && !isAdmin && !isRoomException) {
                statusColor = "#9e9e9e"; statusLabel = "사용 불가"; isClickable = false; backgroundColor = darkMode ? "grey.800" : "#f5f5f5";
                iconComponent = <LockIcon sx={{ color: statusColor, fontSize: "1.4rem" }} />;
              } else if (isRestricted) {
                statusColor = "#9e9e9e"; statusLabel = "레슨실"; isClickable = false; backgroundColor = darkMode ? "grey.800" : "#f5f5f5";
                iconComponent = <LockIcon sx={{ color: statusColor, fontSize: "1.4rem" }} />;
              } else if (existing) {
                if (existing.is_block) {
                  // 관리자 임시 차단 — 회원에게는 사용 불가, 관리자/선생님에게는 (임시) 표기
                  statusColor = "#9e9e9e"; statusLabel = isAdmin ? "사용 불가 (임시)" : "사용 불가"; isClickable = user?.role === "ADMIN"; backgroundColor = darkMode ? "grey.800" : "#f5f5f5";
                } else if (user?.role === "ADMIN") {
                  statusColor = "#ff9800"; statusLabel = "예약: " + (existing.user?.name || "예약자"); backgroundColor = darkMode ? "rgba(255, 243, 224, 0.15)" : "#fff3e0";
                } else {
                  statusColor = "#f44336"; statusLabel = "예약됨"; isClickable = false; backgroundColor = darkMode ? "rgba(255, 235, 238, 0.2)" : "#ffebee";
                }
                iconComponent = <LockIcon sx={{ color: statusColor, fontSize: "1.4rem" }} />;
              }

              return (
                <Box
                  key={room}
                  onClick={() => {
                    // 본인 예약이면 취소 가능 (사용 불가 시간이더라도)
                    if (isMine) {
                      handleCancel(existing?.id);
                      return;
                    }
                    if (isRestricted || (isRoomBlocked && !isAdmin)) return;
                    if (isAvailable) {
                      handleRoomSelect(room);
                      return;
                    }
                    if (user?.role === "ADMIN" && existing) {
                      handleCancel(existing?.id);
                      return;
                    }
                    // 표시상 '예약 가능'이지만 평일 당일 예약 규칙으로 아직 예약할 수 없는 경우 안내
                    if (!isAdmin && !weekdayAllowed && !existing && (!isUnavailableTime || isRoomException)) {
                      const msg = getWeekdayRestrictionMessage(selectedDateObj);
                      if (msg) toast(msg, "info");
                    }
                  }}
                  sx={{
                    p: { xs: 1.25, sm: 2 },
                    borderRadius: 2,
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    gap: 0.5,
                    cursor: (isClickable || user?.role === "ADMIN" || isMine) && !isRestricted && (isMine || !(isRoomBlocked && !isAdmin)) ? "pointer" : "default",
                    background: backgroundColor,
                    border: darkMode ? "2px solid rgba(255, 255, 255, 0.12)" : "2px solid #e8e8e8",
                    transition: "box-shadow 0.2s ease",
                    "&:hover": ((isClickable || user?.role === "ADMIN" || isMine) && !isRestricted && (isMine || !(isRoomBlocked && !isAdmin))) ? { boxShadow: darkMode ? "0 4px 12px rgba(0, 0, 0, 0.4)" : "0 4px 12px rgba(0, 0, 0, 0.12)" } : {},
                  }}
                >
                  {iconComponent}
                  <Typography fontWeight="bold" noWrap sx={{ width: "100%", fontSize: { xs: "0.82rem", sm: "0.95rem" } }}>{room}</Typography>
                  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, px: 0.75, py: 0.3, borderRadius: 1, background: `${statusColor}15`, border: `1px solid ${statusColor}40`, maxWidth: "100%" }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: statusColor, flexShrink: 0 }} />
                    <Typography noWrap sx={{ color: statusColor, fontWeight: 700, fontSize: { xs: "0.6rem", sm: "0.68rem" }, minWidth: 0 }}>{statusLabel}</Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
    </PullToRefresh>
  );
}

export default PracticePage;